import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Loader } from 'lucide-react';
import { Loader as GoogleLoader } from '@googlemaps/js-api-loader';
import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface Address {
  id: string;
  label: string;
  address: string;
  lat: number;
  lng: number;
}

interface AddressModalProps {
  address?: Address;
  onSave: (address: Address) => void;
  onClose: () => void;
}

export const AddressModal: React.FC<AddressModalProps> = ({ address: editAddress, onSave, onClose }) => {
  const { user } = useAuth();
  const [label, setLabel] = useState(editAddress?.label || '');
  const [selectedAddress, setSelectedAddress] = useState(editAddress?.address || '');
  const [selectedLocation, setSelectedLocation] = useState({
    lat: editAddress?.lat || -1.2921,
    lng: editAddress?.lng || 36.8219
  });
  const [mapError, setMapError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<google.maps.places.SearchBox>();
  const mapInstanceRef = useRef<google.maps.Map>();
  const markerRef = useRef<google.maps.Marker>();

  useEffect(() => {
    const initMap = async () => {
      try {
        const loader = new GoogleLoader({
          apiKey: 'AIzaSyAsRCXneY-jFBvvY1a4IqFaCsLICa5xZ7k',
          version: 'weekly',
          libraries: ['places']
        });

        await loader.load();
        
        if (!mapRef.current) return;

        const map = new google.maps.Map(mapRef.current, {
          center: selectedLocation,
          zoom: 15,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        const marker = new google.maps.Marker({
          position: selectedLocation,
          map,
          draggable: true,
          animation: google.maps.Animation.DROP
        });

        const input = document.getElementById('address-search') as HTMLInputElement;
        const searchBox = new google.maps.places.SearchBox(input);
        
        map.addListener('bounds_changed', () => {
          searchBox.setBounds(map.getBounds() as google.maps.LatLngBounds);
        });

        searchBox.addListener('places_changed', () => {
          const places = searchBox.getPlaces();
          if (!places || places.length === 0) return;

          const place = places[0];
          if (!place.geometry || !place.geometry.location) return;

          const newLocation = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };

          map.setCenter(newLocation);
          marker.setPosition(newLocation);
          setSelectedLocation(newLocation);
          setSelectedAddress(place.formatted_address || '');
        });

        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          if (!position) return;

          const newLocation = {
            lat: position.lat(),
            lng: position.lng()
          };

          setSelectedLocation(newLocation);
          
          // Reverse geocode the new position
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: newLocation }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              setSelectedAddress(results[0].formatted_address);
            }
          });
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;
        searchBoxRef.current = searchBox;

      } catch (error) {
        console.error('Error loading map:', error);
        setMapError('Failed to load map. Please try again later.');
      }
    };

    initMap();
  }, [selectedLocation]);

  const handleSave = async () => {
    if (!label || !selectedAddress) return;
    
    setIsSaving(true);
    try {
      // Create form data
      const formData = new URLSearchParams();
      formData.append('user_id', user?.user_id || '');
      formData.append('label', label);
      formData.append('place', selectedAddress);
      formData.append('latitude', selectedLocation.lat.toString());
      formData.append('longitude', selectedLocation.lng.toString());

      if (editAddress) {
        formData.append('address_id', editAddress.id);
      }

      const endpoint = editAddress ? API_ENDPOINTS.EDIT_ADDRESS : API_ENDPOINTS.ADD_ADDRESS;
      
      const response = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        mode: 'cors',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        onSave({
          id: editAddress?.id || data.data.address_id,
          label,
          address: selectedAddress,
          lat: selectedLocation.lat,
          lng: selectedLocation.lng
        });
        onClose();
      } else {
        setMapError('Failed to save address. Please try again.');
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setMapError('Failed to save address. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editAddress ? 'Edit Address' : 'Add New Address'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Home, Office"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search Location
            </label>
            <div className="relative">
              <input
                id="address-search"
                type="text"
                placeholder="Search for a location"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
              />
              <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>

          {mapError ? (
            <div className="text-red-600 dark:text-red-400 text-sm mb-4">{mapError}</div>
          ) : (
            <div ref={mapRef} className="w-full h-64 rounded-lg mb-4" />
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !label || !selectedAddress}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <Loader className="animate-spin h-4 w-4" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Address</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};